import { getStarlinkSnapshot, type PeriodId, type StarlinkKpi } from '@/components/value-chain/starlinkProduct';

export type ConsumerTabId =
  | 'overview'
  | 'products'
  | 'value-chain'
  | 'suppliers'
  | 'geography'
  | 'key-data'
  | 'documents';

export type ConsumerProductKind =
  | 'standard-4x'
  | 'standard-4'
  | 'mini'
  | 'router-3'
  | 'router-mini'
  | 'mounts'
  | 'app';

export type ConsumerStreamId = 'consumer' | 'enterprise' | 'hardware';

export type ConsumerProduct = {
  id: ConsumerProductKind;
  name: string;
  badge: string;
  detail: string;
  mappingLabel?: string;
};

export const CONSUMER_TABS: Array<{ id: ConsumerTabId; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'products', label: 'Products' },
  { id: 'value-chain', label: 'Value Chain' },
  { id: 'suppliers', label: 'Suppliers' },
  { id: 'geography', label: 'Geography' },
  { id: 'key-data', label: 'Key Data' },
  { id: 'documents', label: 'Documents' },
];

export const CONSUMER_TAGLINE = 'Products that enable global internet access for individuals and small businesses.';

export const CONSUMER_DESCRIPTION =
  'Consumer revenue is primarily driven by monthly service subscriptions using Starlink hardware. This section shows the key physical products that enable the Consumer revenue stream.';

export const CONSUMER_PRODUCTS: ConsumerProduct[] = [
  {
    id: 'standard-4x',
    name: 'Standard 4X',
    badge: 'Primary (Residential)',
    detail: 'High-performance terminal for home use. Latest generation with higher speeds and improved antenna.',
  },
  {
    id: 'standard-4',
    name: 'Standard 4',
    badge: 'Residential / Roam',
    detail: 'Next-generation standard terminal for home and flexible use.',
  },
  {
    id: 'mini',
    name: 'Mini',
    badge: 'Roam / Residential',
    detail: 'Portable, compact terminal designed for travel, RVs, and on-the-go use.',
  },
  {
    id: 'router-3',
    name: 'Router 3',
    badge: 'Wi-Fi',
    detail: 'High-performance Wi-Fi router included with Standard 4X.',
  },
  {
    id: 'router-mini',
    name: 'Router Mini',
    badge: 'Wi-Fi',
    detail: 'Compact Wi-Fi router included with Standard 4 and Mini.',
  },
  {
    id: 'mounts',
    name: 'Mounts & Accessories',
    badge: 'Installation',
    detail: 'Roof, wall, pole, and portable mounts, cables, power supplies, and accessories.',
  },
  {
    id: 'app',
    name: 'Starlink App',
    badge: 'Activation / Management',
    detail: 'Used for setup, account management, network monitoring, and plan changes.',
  },
];

export const CONSUMER_MAPPING_ROWS: Array<{
  id: string;
  label: string;
  sublabel?: string;
  kind: ConsumerProductKind;
  streams: ConsumerStreamId[];
}> = [
  { id: 'map-4x', label: 'Standard 4X', kind: 'standard-4x', streams: ['consumer', 'enterprise'] },
  { id: 'map-4', label: 'Standard 4', kind: 'standard-4', streams: ['consumer', 'enterprise'] },
  { id: 'map-mini', label: 'Mini', kind: 'mini', streams: ['consumer', 'enterprise'] },
  {
    id: 'map-routers',
    label: 'Routers',
    sublabel: '(Router 3 / Router Mini)',
    kind: 'router-3',
    streams: ['consumer', 'enterprise', 'hardware'],
  },
  { id: 'map-mounts', label: 'Mounts & Accessories', kind: 'mounts', streams: ['hardware', 'enterprise'] },
  { id: 'map-app', label: 'Starlink App', kind: 'app', streams: ['hardware'] },
];

export const CONSUMER_TAKEAWAYS = [
  'Consumer revenue is driven by service subscriptions using Starlink hardware, primarily Standard 4X, Standard 4, and Mini.',
  'The same hardware can be used across multiple consumer use cases (residential, roam, and self-serve business).',
  'Routers, mounts, and accessories are essential parts of the consumer product ecosystem.',
  'Specific plan-level revenue breakdowns (e.g., Residential vs. Roam) are not disclosed by SpaceX.',
];

export const CONSUMER_LINKS: Array<{ label: string; href: string }> = [
  { label: 'Starlink Hardware Specifications', href: 'https://www.starlink.com/specifications' },
  { label: 'Starlink Support & Setup', href: 'https://www.starlink.com/support' },
  {
    label: 'Q2 2026 Earnings Release',
    href: 'https://www.sec.gov/Archives/edgar/data/1181412/000162828026052515/earningsreleaseq22608042.htm',
  },
  {
    label: 'Form 10-Q (Jun 30, 2026)',
    href: 'https://www.sec.gov/Archives/edgar/data/1181412/000162828026052535/spcx-20260630.htm',
  },
];

export const CONSUMER_KEY_ROWS: Array<{ metric: string; value: string; source: string }> = [
  { metric: 'Consumer Q2 2026', value: '$2,485 million', source: 'SPCX 10-Q, Note 3' },
  { metric: 'Consumer Q2 2025', value: '$1,721 million', source: 'SPCX 10-Q, Note 3' },
  { metric: 'Consumer H1 2026', value: '$4,633 million', source: 'SPCX 10-Q, Note 3' },
  { metric: 'Consumer Q1 2026 (implied)', value: '$2,148 million', source: 'H1 $4,633 − Q2 $2,485 (Note 3)' },
  { metric: 'Consumer YoY (Q2)', value: '+44.4%', source: 'SPCX 10-Q, Note 3' },
  { metric: 'Starlink subscribers Jun 30, 2026', value: '12.0 million EoP', source: 'SPCX 10-Q, Key Business Metrics' },
  { metric: 'Starlink subscribers Jun 30, 2025', value: '6.0 million EoP', source: 'SPCX 10-Q, Key Business Metrics' },
  { metric: 'ARPU Q2 / H1 2026', value: '$66 / month', source: 'SPCX 10-Q, Key Business Metrics' },
  { metric: 'ARPU Q2 / H1 2025', value: '$85 / month', source: 'SPCX 10-Q, Key Business Metrics' },
  { metric: 'Residential vs Roam mix', value: 'Unprinted', source: 'Not disclosed in the 10-Q' },
];

export function getConsumerKpis(periodId: PeriodId = 'q2-2026'): StarlinkKpi[] {
  const snapshot = getStarlinkSnapshot(periodId);
  const consumer = snapshot.segments.find((segment) => segment.id === 'consumer');
  const subscribers = snapshot.kpis.find((kpi) => kpi.label.startsWith('Subscribers'));
  const arpu = snapshot.kpis.find((kpi) => kpi.label.startsWith('ARPU'));
  return [
    {
      value: consumer?.display ?? '$2.485B',
      label: 'Consumer Revenue (TTM)',
      delta: '+44% YoY',
    },
    {
      value: subscribers?.value ?? '12.0M',
      label: 'Subscribers (EoP)',
      delta: '+100% YoY',
    },
    {
      value: arpu?.value ?? '$66',
      label: 'ARPU (Monthly)',
      delta: '−22% YoY',
      down: true,
    },
  ];
}

export function getConsumerStreams(periodId: PeriodId = 'q2-2026') {
  const snapshot = getStarlinkSnapshot(periodId);
  const byId = Object.fromEntries(snapshot.segments.map((segment) => [segment.id, segment]));
  return [
    {
      id: 'consumer' as const,
      label: 'Consumer',
      display: byId.consumer?.display ?? '$2.485B',
      detail: 'Residential, Roam, and self-serve business',
      color: '#2563eb',
      icon: 'home' as const,
    },
    {
      id: 'enterprise' as const,
      label: 'Enterprise & Government',
      display: byId.enterprise?.display ?? '$1.806B',
      detail: 'Aviation, maritime, land mobility, fixed sites, and government',
      color: '#059669',
      icon: 'building' as const,
    },
    {
      id: 'hardware' as const,
      label: 'Connectivity Products (Hardware)',
      display: byId.hardware?.display ?? '$461M',
      detail: 'Starlink terminals, routers, mounts, and accessories',
      color: '#7c3aed',
      icon: 'package' as const,
    },
  ];
}

export function isStarlinkConsumerId(id: string): boolean {
  return id === 'starlink-consumer' || id === 'consumer';
}
